import React, { useState, useEffect } from "react";
import Select from "react-select"; // Import react-select components
import { IconButton } from "@mui/material";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { display } from "@mui/system";
import zIndex from "@mui/material/styles/zIndex";

const CustomPagination = ({
    totalRecords,
    rowsPerPageOptions = [10, 25, 50],
    initialRowsPerPage = 10,
    setRowsPerPage1,
    initialPage = 0,
    onPageChange,
    onRowsPerPageChange,
  }) => {
    const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage);
    const [currentPage, setCurrentPage] = useState(initialPage + 1);
    
  
    useEffect(() => {
      setCurrentPage(initialPage === -1 ? 1 : initialPage + 1); // Reset with 1-based index
    }, [initialPage]);
  
    const totalPages = Math.ceil(totalRecords / rowsPerPage);
  
    // Handle page change
    const handlePageChange = (newPage) => {
      if (newPage > 0 && newPage <= totalPages) {
        setCurrentPage(newPage);
        onPageChange?.(newPage); // Call onPageChange with 1-based index
      }
    };
  
    // Handle rows per page change
    const handleRowsPerPageChange = (selectedOption) => {
      const newRowsPerPage = selectedOption.value;
      setRowsPerPage1(newRowsPerPage);
      setRowsPerPage(newRowsPerPage);
      setCurrentPage(1); // Reset to the first page when rows per page changes
      onRowsPerPageChange?.(newRowsPerPage);
    };
  
    // Adjusted start and end record calculations
    const startRecord = (currentPage - 1) * rowsPerPage + 1;
    const endRecord = Math.min(totalRecords, currentPage * rowsPerPage); // Correct end for the current page
  
    // Prepare options for react-select
    const rowsPerPageOptionsFormatted = rowsPerPageOptions.map((option) => ({
      value: option,
      label: `${option}`,
    }));
  
    return (
      <div style={styles.paginationContainer}>
        {/* Rows per page selector */}
        <div style={styles.rowsPerPage}>
          <span>Rows per page:</span>
          <Select
            options={rowsPerPageOptionsFormatted}
            value={{ value: rowsPerPage, label: `${rowsPerPage}` }}
            onChange={handleRowsPerPageChange}
            styles={customStyles}
            menuPlacement="top"
            menuPortalTarget={document.body}
          />
        </div>
  
        {/* Current page range */}
        <div style={styles.pageInfo}>
          {startRecord}–{endRecord} of {totalRecords}
        </div>
  
        {/* Pagination buttons */}
        <div style={styles.navigationButtons}>
          <IconButton
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1} // Disable if on the first page
          >
            <ChevronLeft />
          </IconButton>
          <IconButton
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages} // Disable if on the last page
          >
            <ChevronRight />
          </IconButton>
        </div>
      </div>
    );
  };
  
  
// Custom styles for react-select
const customStyles = {
  control: (provided) => ({
    ...provided,
    minWidth: 100,
    height: 25,
    borderRadius: "8px",
    border: "1px solid #ccc",
    padding: "3px 8px",
    backgroundColor: "#f8f8f8",
    transition: "all 0.3s ease",
    zIndex: 1, // Set a lower z-index for the control itself
    "&:hover": {
      borderColor: "#888",
    },
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999, // Ensure the dropdown stays on top
  }),
  menu: (provided) => ({
    ...provided,
    borderRadius: "8px",
    backgroundColor: "#f8f8f8",
    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
    transition: "all 0.3s ease",
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? "#ccc" : "#f8f8f8",
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "#ddd",
    },
  }),
  singleValue: (provided) => ({
    ...provided,
    color: "#333",
    fontSize: "14px",
  }),
  placeholder: (provided) => ({
    ...provided,
    color: "#888",
    fontSize: "14px",
  }),
};

const styles = {
  paginationContainer: {
    display: "flex",
    flexDirection: "row", // Items in a row
    alignItems: "center", // Center vertically
    justifyContent: "center", // Center horizontally
    gap: "1rem", // Space between items
    fontWeight: 600,
    fontFamily: "Urbane-Light",
    backgroundColor: "#ECF1FF",
    fontSize: "12px",
    lineHeight: "15px",
    color: "#202020",
    padding: "1rem",
    borderBottom: "1px solid #AED9FF",
    border: "1px solid #ddd",
    textAlign: "center",
    textTransform: "capitalize",
    overflowX: "auto", // Enable horizontal scrolling if needed
    whiteSpace: "nowrap", // Prevent wrapping of items
  },
  rowsPerPage: {
    display: "flex",
    alignItems: "center", // Center text and select
    gap: "8px",
  },
  pageInfo: {
    textAlign: "center", // Center text
    fontSize: "12px",
  },
  navigationButtons: {
    display: "flex",
    alignItems: "center", // Center icons vertically
    justifyContent: "center", // Center icons horizontally
    gap: "12px",
  },
  iconButton: {
    fontSize: "18px",
    color: "#333",
    fontWeight: "bold",
    borderRadius: "50%",
    padding: "6px",
    backgroundColor: "#fff",
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
    transition: "all 0.3s ease",
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "#f0f0f0",
    },
    "&.Mui-disabled": {
      color: "#888",
      backgroundColor: "#e0e0e0",
      boxShadow: "none",
      cursor: "not-allowed",
    },
  },
  "@media (max-width: 768px)": {
    paginationContainer: {
      flexDirection: "column", // Stack items vertically
    },
  },
};

export default CustomPagination;
